// The browser's half of the push channel: what state it is in, and turning it on and off. Everything from the browser comes in through PushEnv,
// so this runs in a test. Nothing here asks for permission on its own: only turnPushOn does, and only a tap calls that.
import type { NotifyPreferencesDto, PushSubscribedDto, PushSubscribeRequest, UiLang } from "@imagora/contracts";

export type PushState =
  /** This deployment has no keys. */
  | "no_keys"
  /** Inside a host's page: a frame can't hold this, the app opened on its own can. */
  | "embedded"
  /** An iPhone or iPad gives push only to an app on the home screen. */
  | "needs_install"
  | "unsupported"
  | "denied"
  | "off"
  | "on";

export interface PushSubscriptionLike {
  endpoint: string;
  options?: { applicationServerKey?: ArrayBuffer | null };
  toJSON(): { endpoint?: string; keys?: Record<string, string> };
  unsubscribe(): Promise<boolean>;
}

export interface PushRegistrationLike {
  pushManager: {
    getSubscription(): Promise<PushSubscriptionLike | null>;
    subscribe(options: { userVisibleOnly: true; applicationServerKey: Uint8Array }): Promise<PushSubscriptionLike>;
  };
}

export interface PushEnv {
  embedded: boolean;
  ios: boolean;
  standalone: boolean;
  supported: boolean;
  permission(): "default" | "denied" | "granted";
  askPermission(): Promise<"default" | "denied" | "granted">;
  existing(): Promise<PushRegistrationLike | null>;
  register(): Promise<PushRegistrationLike>;
  load(): string | null;
  save(value: string | null): void;
}

export interface PushApi {
  subscribePush(body: PushSubscribeRequest): Promise<PushSubscribedDto>;
  removePush(id: string): Promise<NotifyPreferencesDto>;
}

export interface SavedDevice {
  id: string;
  token: string;
  endpoint: string;
}

const SCRIPT = "/push/sw.js";
const SCOPE = "/push/";
const ACTIVATE_TIMEOUT_MS = 10_000;

export const isIos = (userAgent: string, maxTouchPoints: number): boolean => /iPad|iPhone|iPod/u.test(userAgent) || (/Macintosh/u.test(userAgent) && maxTouchPoints > 1);

const fromB64Url = (text: string): Uint8Array => {
  const binary = atob(text.replace(/-/gu, "+").replace(/_/gu, "/"));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

function savedDevices(env: PushEnv): Record<string, SavedDevice> {
  try {
    const parsed: unknown = JSON.parse(env.load() ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Record<string, SavedDevice>) : {};
  } catch {
    return {};
  }
}

function writeDevices(env: PushEnv, devices: Record<string, SavedDevice>): void {
  env.save(Object.keys(devices).length > 0 ? JSON.stringify(devices) : null);
}

export const savedDeviceOf = (env: PushEnv, tenantId: string): SavedDevice | null => savedDevices(env)[tenantId] ?? null;

/**
 * Which way in a tap can use. Asked for a business, that business's. Asked for none, only an installed app answers, since it has no address
 * bar to open a sign-in link in: a tab in a browser stays signed out until something was tapped.
 */
export function deviceTokenFor(env: PushEnv, tenantId: string | null): string | null {
  const devices = savedDevices(env);
  if (tenantId) return devices[tenantId]?.token ?? null;
  return env.standalone ? (Object.values(devices).at(-1)?.token ?? null) : null;
}

export function forgetDeviceToken(env: PushEnv, token: string): void {
  writeDevices(env, Object.fromEntries(Object.entries(savedDevices(env)).filter(([, device]) => device.token !== token)));
}

function sameKey(subscription: PushSubscriptionLike, publicKey: string): boolean {
  const current = subscription.options?.applicationServerKey;
  if (!current) return true;
  const wanted = fromB64Url(publicKey);
  const bytes = new Uint8Array(current);
  return bytes.length === wanted.length && bytes.every((byte, index) => byte === wanted[index]);
}

async function currentSubscription(env: PushEnv): Promise<PushSubscriptionLike | null> {
  const registration = await env.existing();
  return registration ? registration.pushManager.getSubscription() : null;
}

/** Reads, and never asks anything. */
export async function readPushState(env: PushEnv, input: { publicKey: string | null; devices: ReadonlyArray<{ id: string }>; tenantId: string }): Promise<PushState> {
  if (!input.publicKey) return "no_keys";
  if (env.embedded) return "embedded";
  if (env.ios && !env.standalone) return "needs_install";
  if (!env.supported) return "unsupported";
  if (env.permission() === "denied") return "denied";
  const saved = savedDeviceOf(env, input.tenantId);
  if (!saved || !input.devices.some((device) => device.id === saved.id)) return "off";
  if (env.permission() !== "granted") return "off";
  const subscription = await currentSubscription(env);
  return subscription && sameKey(subscription, input.publicKey) ? "on" : "off";
}

function subscribeBody(subscription: PushSubscriptionLike, lang: UiLang): PushSubscribeRequest {
  const json = subscription.toJSON();
  return { endpoint: subscription.endpoint, keys: { p256dh: json.keys?.["p256dh"] ?? "", auth: json.keys?.["auth"] ?? "" }, lang };
}

async function report(env: PushEnv, api: PushApi, tenantId: string, subscription: PushSubscriptionLike, lang: UiLang): Promise<PushSubscribedDto> {
  const made = await api.subscribePush(subscribeBody(subscription, lang));
  writeDevices(env, { ...savedDevices(env), [tenantId]: { id: made.deviceId, token: made.deviceToken, endpoint: subscription.endpoint } });
  return made;
}

export type TurnOnResult = { outcome: "on"; preferences: NotifyPreferencesDto } | { outcome: "denied" } | { outcome: "dismissed" };

/** Called from a tap, and asks first: a browser takes the question only inside the tap itself. */
export async function turnPushOn(env: PushEnv, api: PushApi, input: { publicKey: string; tenantId: string; lang: UiLang }): Promise<TurnOnResult> {
  const permission = env.permission() === "granted" ? "granted" : await env.askPermission();
  if (permission === "denied") return { outcome: "denied" };
  if (permission !== "granted") return { outcome: "dismissed" };
  const registration = await env.register();
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription, input.publicKey)) {
    // The keys were changed: a subscription made for the old ones can never be delivered to.
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromB64Url(input.publicKey) });
  return { outcome: "on", preferences: (await report(env, api, input.tenantId, subscription, input.lang)).preferences };
}

/** This business's row goes. The browser's own subscription goes too, unless another business on this phone still uses it. */
export async function turnPushOff(env: PushEnv, api: PushApi, input: { tenantId: string }): Promise<NotifyPreferencesDto | null> {
  const devices = savedDevices(env);
  const saved = devices[input.tenantId];
  const preferences = saved ? await api.removePush(saved.id) : null;
  const { [input.tenantId]: _gone, ...others } = devices;
  writeDevices(env, others);
  if (Object.keys(others).length === 0) await (await currentSubscription(env))?.unsubscribe();
  return preferences;
}

/** Signing out of a phone: nothing it holds should open anything or ring for anyone. Other businesses' rows go at their next send, on the 410. */
export async function forgetPushOnSignOut(env: PushEnv, api: PushApi, input: { tenantId: string }): Promise<void> {
  const saved = savedDeviceOf(env, input.tenantId);
  writeDevices(env, {});
  await Promise.allSettled([saved ? api.removePush(saved.id) : null, currentSubscription(env).then((subscription) => subscription?.unsubscribe())]);
}

/** On opening the app. A browser may change the address of a subscription at any time; the worker does nothing but show notices, so it is told here. */
export async function syncPushOnOpen(env: PushEnv, api: PushApi, input: { tenantId: string; lang: UiLang }): Promise<void> {
  const saved = savedDeviceOf(env, input.tenantId);
  if (!saved || env.embedded || !env.supported || env.permission() !== "granted") return;
  const subscription = await currentSubscription(env);
  if (subscription && subscription.endpoint !== saved.endpoint) await report(env, api, input.tenantId, subscription, input.lang);
}

export type PushOpenAction = { kind: "go"; path: string } | { kind: "reload"; href: string };

/** The worker told an open window about a tap. The same business moves inside the app; another one needs that business's session, so a fresh start. */
export function pushOpenAction(data: unknown, currentTenant: string | null): PushOpenAction | null {
  const message = data as { type?: unknown; path?: unknown; tenant?: unknown } | null;
  if (message?.type !== "imagora:open" || typeof message.path !== "string" || !/^\/(?![/\\])[^\s\\]*$/u.test(message.path)) return null;
  if (typeof message.tenant !== "string" || !message.tenant || message.tenant === currentTenant) return { kind: "go", path: message.path };
  return { kind: "reload", href: `${message.path}${message.path.includes("?") ? "&" : "?"}tenant=${encodeURIComponent(message.tenant)}` };
}

/** What the real browser offers. The only part of this file that touches it. */
export function browserPushEnv(embedded: boolean): PushEnv {
  const key = "imagora.push-device";
  const standalone = (): boolean => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
  return {
    embedded,
    ios: isIos(navigator.userAgent, navigator.maxTouchPoints),
    standalone: standalone(),
    supported: "serviceWorker" in navigator && "PushManager" in window && "Notification" in window,
    permission: () => Notification.permission,
    askPermission: () => Notification.requestPermission(),
    existing: async () => ("serviceWorker" in navigator ? (((await navigator.serviceWorker.getRegistration(SCOPE)) as PushRegistrationLike | undefined) ?? null) : null),
    async register() {
      const registration = await navigator.serviceWorker.register(SCRIPT, { scope: SCOPE });
      const worker = registration.installing ?? registration.waiting;
      if (!registration.active && worker) {
        // Not `serviceWorker.ready`: that one waits for a worker controlling this page, and this worker's scope leaves every page out, on purpose.
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error("push_worker_timeout")), ACTIVATE_TIMEOUT_MS);
          worker.addEventListener("statechange", () => {
            if (worker.state === "activated" || worker.state === "activating") {
              clearTimeout(timer);
              resolve();
            } else if (worker.state === "redundant") {
              clearTimeout(timer);
              reject(new Error("push_worker_failed"));
            }
          });
        });
      }
      return registration as unknown as PushRegistrationLike;
    },
    load() {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    save(value) {
      try {
        if (value) window.localStorage.setItem(key, value);
        else window.localStorage.removeItem(key);
      } catch {
        // A browser that keeps nothing: a tap asks for a sign-in instead.
      }
    },
  };
}
